import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  { id: 1, q: "Is every AI decision bound to a signed, tamper-evident record at the moment it is made?",
    module: "VeriAuth · VeriCert", reg: "RBI FREE-AI (assurance) · DPDP s.8(5)", statutory: true },
  { id: 2, q: "Is each model fingerprinted (hashed) and verified against a registry before it runs?",
    module: "VeriAuth · VeriScan", reg: "RBI FREE-AI (model-risk) · DPDP s.8(5)", statutory: true },
  { id: 3, q: "Are your decision / audit records append-only, with no ability to DELETE or UPDATE them?",
    module: "VeriAuth · VeriVault", reg: "RBI FREE-AI (assurance) · DPDP s.8(5)", statutory: true },
  { id: 4, q: "Beyond application logs, do you persist each decision to an immutable, chain-hashed store?",
    module: "VeriAuth · VeriVault", reg: "RBI FREE-AI (assurance)", statutory: false },
  { id: 5, q: "When data reaches an external AI service, do you attest which model / version was used per decision?",
    module: "VeriAuth · VeriCert", reg: "DPDP s.8(5) · s.8(6) breach notification", statutory: true },
  { id: 6, q: "Can you produce a regulator-ready audit export of attested decisions on demand?",
    module: "VeriAuth · VeriReport", reg: "RBI FREE-AI (assurance)", statutory: false },
  { id: 7, q: "Can an independent third party verify a decision without access to your systems or trust in you?",
    module: "VeriAuth · Independent verifier", reg: "RBI FREE-AI (assurance)", statutory: false },
  { id: 8, q: "Can you honour a data-erasure request while retaining tamper-evident proof of the record?",
    module: "VeriAuth · Key-shredding", reg: "DPDP s.8(5) · retention vs. erasure", statutory: true },
];

const OPTS = [["yes", "Yes"], ["partial", "Partially"], ["no", "No"]];
const SCORE = { yes: 1, partial: 0.5, no: 0 };
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : "a" + Date.now() + Math.random().toString(16).slice(2));

const BrandHead = ({ navigate, sub }) => (
  <div className="brand" style={{ display: "flex", alignItems: "center", gap: 11 }}>
    <Logo w={34} h={28} />
    <a href="/" className="name" style={{ textDecoration: "none" }} onClick={(e) => { e.preventDefault(); navigate("/"); }}>
      veri<span className="g">Auth</span><span className="d">.ai</span></a>
    <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--dim)", textAlign: "right" }}>
      <a href="/" style={{ color: "var(--dim)" }} data-testid="assess-back" onClick={(e) => { e.preventDefault(); navigate("/"); }}>← Back to site</a><br />
      {sub}
    </div>
  </div>
);

export default function Assessment() {
  const navigate = useNavigate();
  const [step, setStep] = useState("lead");   // lead | quiz | done
  const [lead, setLead] = useState({ name: "", email: "", company: "", role: "" });
  const [leadId, setLeadId] = useState(null);
  const [leadErr, setLeadErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [answers, setAnswers] = useState({});

  const setL = (k) => (e) => setLead({ ...lead, [k]: e.target.value });

  const answered = Object.keys(answers).length;
  const pct = Math.round((answered / QUESTIONS.length) * 100);
  const pick = (qid, val) => setAnswers({ ...answers, [qid]: val });

  const gaps = QUESTIONS.filter((q) => answers[q.id] && answers[q.id] !== "yes");
  const raw = QUESTIONS.reduce((s, q) => s + (SCORE[answers[q.id]] ?? 0), 0);
  const readiness = Math.round((raw / QUESTIONS.length) * 100);
  const statutoryHit = gaps.some((g) => g.statutory);
  const exposure = statutoryHit
    ? "Up to ₹250 cr (s.8(5)) + ₹200 cr (s.8(6)) per violation — statutory ceilings, discretionary"
    : "No statutory clause implicated by your answers";

  const startTest = async (e) => {
    e.preventDefault();
    setLeadErr("");
    if (!lead.name.trim() || !EMAIL_RE.test(lead.email.trim())) {
      setLeadErr("Please enter your name and a valid work email."); return;
    }
    if (!lead.company.trim() || !lead.role.trim()) {
      setLeadErr("Please enter your company and designation."); return;
    }
    const id = newId();
    setLeadId(id);
    setBusy(true);
    try {
      await axios.post(`${API}/assessment`, {
        id, name: lead.name.trim(), email: lead.email.trim(),
        company: lead.company.trim(), role: lead.role.trim(), completed: false,
      });
    } catch { /* non-blocking: still let them take the test */ }
    finally { setBusy(false); }
    setStep("quiz");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const finish = () => {
    setStep("done");
    window.scrollTo({ top: 0, behavior: "smooth" });
    axios.post(`${API}/assessment`, {
      id: leadId || newId(), name: lead.name.trim(), email: lead.email.trim(),
      company: lead.company.trim(), role: lead.role.trim(),
      score: readiness, gaps: gaps.length, completed: true,
    }).catch(() => {});
  };

  const download = () => {
    const rows = QUESTIONS.map((q) => {
      const a = answers[q.id] || "unanswered";
      return `Q${q.id}. ${q.q}\n   Answer: ${a.toUpperCase()}  |  ${q.module}  |  ${q.reg}`;
    }).join("\n\n");
    const txt = `VeriAuth — AI Decision Readiness Self-Assessment\n${new Date().toUTCString()}\n\n` +
      `Name:        ${lead.name}\nEmail:       ${lead.email}\nCompany:     ${lead.company}\nDesignation: ${lead.role}\n\n` +
      `Readiness score: ${readiness}%\nGaps identified: ${gaps.length} of ${QUESTIONS.length}\n` +
      `Statutory exposure: ${exposure}\n\n${"=".repeat(60)}\n\n${rows}\n\n${"=".repeat(60)}\n` +
      `Sources: RBI FREE-AI (13 Aug 2025, advisory); DPDP Act 2023 Schedule & s.33 (statutory maxima, discretionary).\n` +
      `Figures are statutory ceilings, not predictions. This is not legal advice. © 2026 VeriAuth Technologies Pvt. Ltd.`;
    const blob = new Blob([txt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "veriauth_readiness_report.txt"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="wrap" style={{ maxWidth: 820, paddingTop: 28, paddingBottom: 72 }}>
      <BrandHead navigate={navigate} sub="Readiness self-assessment" />

      {/* STEP 1 — lead capture */}
      {step === "lead" && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="eyebrow" style={{ marginTop: 24 }}>NO ACCESS · ~2 MIN</div>
          <h1 style={{ fontSize: "clamp(26px,4vw,34px)", color: "var(--head)", margin: "12px 0 8px", letterSpacing: "-1px" }}>
            Before you begin — who's this for?</h1>
          <p className="lede">A few quick details so we can send your readiness report and follow up with tailored guidance.
            The eight-question test runs entirely in your browser; nothing about your systems leaves your machine.</p>

          <form className="card" onSubmit={startTest} style={{ marginTop: 18 }}>
            <div className="two">
              <div><label htmlFor="a-name">Your name *</label><input id="a-name" data-testid="assess-name" value={lead.name} onChange={setL("name")} /></div>
              <div><label htmlFor="a-email">Work email *</label><input id="a-email" data-testid="assess-email" type="email" placeholder="you@yourcompany.com" value={lead.email} onChange={setL("email")} /></div>
            </div>
            <div className="two">
              <div><label htmlFor="a-company">Company name *</label><input id="a-company" data-testid="assess-company" value={lead.company} onChange={setL("company")} /></div>
              <div><label htmlFor="a-role">Designation *</label><input id="a-role" data-testid="assess-role" placeholder="e.g. Head of Risk, CTO" value={lead.role} onChange={setL("role")} /></div>
            </div>
            <div className="err" data-testid="assess-lead-error">{leadErr}</div>
            <div style={{ marginTop: 14 }}>
              <button className="btn" type="submit" data-testid="assess-start" disabled={busy}>
                {busy ? <><span className="spin" /> Starting…</> : "Start the 2-minute test →"}
              </button>
            </div>
          </form>
          <div className="foot-note">Self-assessment · indicators requiring human review, not an audit. Sources: RBI FREE-AI (advisory);
            DPDP Act 2023 s.33 (statutory maxima, discretionary). Not legal advice.</div>
        </motion.div>
      )}

      {/* STEP 2 — questions */}
      {step === "quiz" && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="eyebrow" style={{ marginTop: 24 }}>NO ACCESS · ~2 MIN</div>
          <h1 style={{ fontSize: "clamp(26px,4vw,34px)", color: "var(--head)", margin: "12px 0 8px", letterSpacing: "-1px" }}>
            Eight questions. Where is your AI unprovable?</h1>
          <p className="lede">Hi {lead.name.split(" ")[0]} — answer honestly about how your AI decisions are recorded today.
            Nothing leaves your machine; scoring happens right here in your browser.</p>

          <div className="progress-bar" style={{ marginTop: 18 }}><i style={{ width: `${pct}%` }} /></div>
          <div className="kv" style={{ marginTop: 6 }} data-testid="assess-progress">{answered} / {QUESTIONS.length} answered</div>

          {QUESTIONS.map((q) => (
            <div className="q" key={q.id} data-testid={`assess-q-${q.id}`}>
              <div className="qn">Q{q.id} · {q.reg}{q.statutory ? " · STATUTORY" : ""}</div>
              <h3>{q.q}</h3>
              <div className="opts">
                {OPTS.map(([val, label]) => (
                  <div key={val} className={"opt" + (answers[q.id] === val ? " sel" : "")}
                    data-testid={`assess-q${q.id}-${val}`} onClick={() => pick(q.id, val)}>{label}</div>
                ))}
              </div>
            </div>
          ))}

          <div style={{ marginTop: 18 }}>
            <button className="btn" data-testid="assess-submit" disabled={answered < QUESTIONS.length} onClick={finish}>
              {answered < QUESTIONS.length ? `Answer all ${QUESTIONS.length} to see your score` : "See my readiness score →"}
            </button>
          </div>
        </motion.div>
      )}

      {/* STEP 3 — results */}
      {step === "done" && (
        <AnimatePresence>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="assess-results">
            <div className="eyebrow" style={{ marginTop: 24 }}>Your readiness score · {lead.company}</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 16, flexWrap: "wrap", marginTop: 10 }}>
              <div className="gauge" data-testid="assess-score" style={{ color: readiness >= 70 ? "var(--green)" : readiness >= 40 ? "var(--amber)" : "var(--red)" }}>{readiness}%</div>
              <div>
                <h1 style={{ fontSize: 22, color: "var(--head)", margin: 0 }}>{gaps.length} gap{gaps.length !== 1 ? "s" : ""} where your AI is unprovable</h1>
                <p className="lede" style={{ marginTop: 6 }}>Each gap below maps to the VeriAuth module that closes it and the regulation it implicates.</p>
              </div>
            </div>

            <div className="exp" style={{ marginTop: 16 }}>
              <div className="k">Statutory exposure implicated</div>
              <div className="v">{exposure}</div>
              <div className="fine">DPDP penalties are Schedule-capped maximums under s.33 — per violation, discretionary, not automatic. RBI FREE-AI is advisory. Not legal advice.</div>
            </div>

            <h1 style={{ fontSize: 16, marginTop: 22, color: "var(--head)" }}>Your gaps</h1>
            {gaps.length ? gaps.map((g) => (
              <div className="find" key={g.id} style={{ borderLeftColor: g.statutory ? "#F0736E" : "#F0A85C" }}>
                <div className="top"><div className="title">{g.q}</div>
                  <div className="sev" style={{ color: g.statutory ? "#F0736E" : "#F0A85C" }}>{answers[g.id] === "no" ? "gap" : "partial"}</div></div>
                <div className="desc">{g.reg}</div><div className="mod">{g.module}</div>
              </div>
            )) : <div className="note"><b>No gaps.</b> Every decision path you described is attested, verifiable and append-only. Impressive.</div>}

            <div className="cta">
              <div className="h">Turn this self-assessment into proof.</div>
              <div className="s">VeriScan reads your actual code to confirm these gaps line-by-line, and a 90-day POC closes them on your pipeline. Download your report, then run VeriScan.</div>
              <div style={{ marginTop: 16, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <button className="btn" data-testid="assess-download" onClick={download}>Download report for your board ↓</button>
                <button className="btn ghost" data-testid="assess-to-scan" onClick={() => navigate("/scan")}>Run VeriScan →</button>
              </div>
            </div>
            <div style={{ marginTop: 16 }}><button className="btn ghost" data-testid="assess-restart" onClick={() => { setStep("quiz"); setAnswers({}); }}>Retake assessment</button></div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}
